import { formatPageRange, formatPageTotal } from '@saay/formatting';
import { Button } from '@saay/shadcn/components/button';
import { Pagination, PaginationContent, PaginationItem } from '@saay/shadcn/components/pagination';
import type { ReactNode } from 'react';

import type { PageTotal } from '../api.js';

/**
 * The prototype list footer («نمایش ۱ تا N از M», `.table-footer`): the loaded row count and the first
 * page's capped total (AC §18.19.3). Paging stays cursor-based, so the pager is «نمایش موارد بیشتر».
 */
export function ListFooter({
  pager,
  shown,
  total,
}: {
  readonly pager?: ReactNode;
  readonly shown: number;
  readonly total: PageTotal | undefined;
}) {
  const summary = formatPageRange(shown, total);
  if (summary === undefined && pager === undefined) {
    return null;
  }
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      {summary === undefined ? null : <span aria-live="polite">{summary}</span>}
      {pager}
    </div>
  );
}

/** Cursor «نمایش موارد بیشتر» inside the stock pagination row. */
export function ListLoadMore({
  hasMore,
  loading,
  onLoadMore,
}: {
  readonly hasMore: boolean;
  readonly loading: boolean;
  readonly onLoadMore: () => void;
}) {
  if (!hasMore) {
    return null;
  }
  return (
    <Pagination aria-label="صفحه‌بندی">
      <PaginationContent>
        <PaginationItem>
          <Button
            aria-busy={loading || undefined}
            disabled={loading}
            onClick={onLoadMore}
            type="button"
            variant="outline"
          >
            نمایش موارد بیشتر
          </Button>
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
}

/** The panel eyebrow over a list (prototype «۵۲ پذیرنده»), from the server total only. */
export function totalEyebrow(total: PageTotal | undefined, noun: string): string | undefined {
  return total === undefined ? undefined : `${formatPageTotal(total)} ${noun}`;
}
